import { useState, useEffect, useRef } from 'react';
import { Trans, Plural, useLingui } from '@lingui/react/macro';
import { Avatar, AvatarFallback } from '@pantry/ui';
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from '@headlessui/react';
import {
  MagnifyingGlassIcon,
  ArrowRightStartOnRectangleIcon,
  SunIcon,
  MoonIcon,
  ComputerDesktopIcon,
  Cog6ToothIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/20/solid';
import { Bars3Icon, XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/useAuth';
import { NotificationDropdown } from '../notifications';
import { useTheme } from '../../contexts/ThemeContext';
import { useSelectedHousehold } from '../../contexts/HouseholdContext';
import { useNavigate, Link, useRouterState } from '@tanstack/react-router';
import {
  useProducts,
  useItems,
  useCurrentUser,
  useUnifiedSearch,
  type Product,
  type ItemWithInstanceCounts,
  type ProductGroupSummary,
} from '@pantry/api-client';
import { cn } from '@pantry/ui';
import { useResolveProductName } from '../../hooks/useProductLanguage';
import { ChevronDownIcon, HomeIcon, CheckIcon, ChevronRightIcon } from '@heroicons/react/20/solid';

interface HeaderProps {
  onMenuClick: () => void;
}

export function Header({ onMenuClick: _onMenuClick }: HeaderProps) {
  const { t } = useLingui();
  const { user, signOut } = useAuth();

  // Define navigation inside component so t() macro works for extraction
  const navigation = [
    { name: t`Dashboard`, href: '/dashboard' },
    { name: t`Inventory`, href: '/items' },
    { name: t`Meal Prep`, href: '/meal-prep' },
    { name: t`Households`, href: '/households' },
    { name: t`Statistics`, href: '/statistics' },
  ];
  const { data: currentUser } = useCurrentUser();
  const { theme, setTheme } = useTheme();
  const isAdmin = currentUser?.appRole === 'admin' || currentUser?.appRole === 'superadmin';
  const {
    selectedHousehold,
    households,
    selectHousehold,
    isLoading: householdsLoading,
  } = useSelectedHousehold();
  const navigate = useNavigate();
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const searchRef = useRef<HTMLDivElement>(null);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch items from user's inventory based on search
  const { data: inventoryItems, isLoading: isSearchingItems } = useItems(
    selectedHousehold?.id || '',
    debouncedQuery.length >= 2 ? { search: debouncedQuery, limit: 5 } : undefined
  );

  // Fetch products (for adding new items) based on search
  const { data: productResults, isLoading: isSearchingProducts } = useProducts(
    debouncedQuery.length >= 2 ? { name: debouncedQuery, limit: 5 } : undefined
  );

  // Product groups search (groups-first results)
  const { groups: groupResults, isLoading: isSearchingGroups } = useUnifiedSearch(
    selectedHousehold?.id || '',
    debouncedQuery
  );

  const isSearching = isSearchingItems || isSearchingProducts || isSearchingGroups;

  // Filter products to exclude ones already in inventory
  const inventoryProductIds = new Set(
    inventoryItems?.map((item) => item.product?.id).filter(Boolean)
  );
  const productsToAdd = productResults?.filter((product) => !inventoryProductIds.has(product.id));

  // Close search results when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSearchResults(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const resolveProductName = useResolveProductName();

  const handleSignOut = async () => {
    await signOut();
    navigate({ to: '/login' });
  };

  const handleItemSelect = (item: ItemWithInstanceCounts) => {
    setSearchQuery('');
    setShowSearchResults(false);
    navigate({
      to: '/items/$itemId',
      params: { itemId: item.id },
    });
  };

  const handleAddProduct = (product: Product) => {
    setSearchQuery('');
    setShowSearchResults(false);
    navigate({
      to: '/items',
      search: { add: true, productSearch: resolveProductName(product) },
    });
  };

  const handleGroupSelect = (group: ProductGroupSummary) => {
    setSearchQuery('');
    setShowSearchResults(false);
    navigate({
      to: '/items',
      search: { groupId: group.id },
    });
  };

  const themeOptions = [
    { value: 'light' as const, label: t`Light`, icon: SunIcon },
    { value: 'dark' as const, label: t`Dark`, icon: MoonIcon },
    { value: 'system' as const, label: t`System`, icon: ComputerDesktopIcon },
  ];

  // Get user initials for avatar
  const getInitials = () => {
    if (user?.user_metadata?.name) {
      return user.user_metadata.name
        .split(' ')
        .map((n: string) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    if (user?.email) {
      return user.email[0].toUpperCase();
    }
    return 'U';
  };

  const userName = user?.user_metadata?.name || 'User';
  const userEmail = user?.email || '';

  return (
    <Popover
      as="header"
      className="sticky top-0 z-30 bg-background/80 backdrop-blur-md border-b border-border data-open:fixed data-open:inset-0 data-open:z-40 data-open:overflow-y-auto data-open:bg-background lg:overflow-y-visible data-open:lg:overflow-y-visible"
    >
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative flex h-16 items-center justify-between gap-4 lg:gap-8">
          {/* Mobile menu button */}
          <div className="flex lg:hidden">
            <PopoverButton className="group relative inline-flex items-center justify-center rounded-lg p-2 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors">
              <span className="sr-only">
                <Trans>Open menu</Trans>
              </span>
              <Bars3Icon aria-hidden="true" className="size-6 group-data-open:hidden" />
              <XMarkIcon aria-hidden="true" className="hidden size-6 group-data-open:block" />
            </PopoverButton>
          </div>

          {/* Mobile logo */}
          <div className="flex lg:hidden">
            <Link to="/dashboard" className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-5 w-5"
                >
                  <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
                  <path d="M3 6h18" />
                  <path d="M16 10a4 4 0 0 1-8 0" />
                </svg>
              </div>
            </Link>
          </div>

          {/* Search bar - desktop */}
          <div className="hidden lg:flex flex-1 max-w-lg">
            <div ref={searchRef} className="relative w-full">
              <div className="relative">
                <MagnifyingGlassIcon
                  aria-hidden="true"
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground"
                />
                <input
                  name="search"
                  type="text"
                  placeholder={t`Search inventory or add items...`}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setShowSearchResults(true);
                  }}
                  onFocus={() => setShowSearchResults(true)}
                  className="block w-full rounded-lg border border-border bg-muted/50 py-2 pl-10 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:bg-background focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
                />
              </div>

              {/* Omni-bar search results dropdown */}
              {showSearchResults && searchQuery.length >= 2 && (
                <div className="absolute top-full left-0 right-0 z-50 mt-2 rounded-lg bg-popover border border-border shadow-lg animate-in max-h-96 overflow-y-auto">
                  {isSearching ? (
                    <div className="flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                        />
                      </svg>
                      <Trans>Searching...</Trans>
                    </div>
                  ) : (
                    <>
                      {/* Product Groups section — groups-first search */}
                      {groupResults && groupResults.length > 0 && (
                        <div className="py-2">
                          <p className="px-4 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                            <Trans>Groups</Trans>
                          </p>
                          <ul>
                            {groupResults.slice(0, 5).map((group) => (
                              <li key={group.id}>
                                <button
                                  type="button"
                                  onClick={() => handleGroupSelect(group)}
                                  className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm hover:bg-accent transition-colors"
                                >
                                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted text-lg">
                                    {group.icon || '📦'}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-foreground truncate">
                                      {group.name}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      {group.memberCount}{' '}
                                      <Plural
                                        value={group.memberCount}
                                        one="product"
                                        other="products"
                                      />
                                    </p>
                                  </div>
                                  <ChevronRightIcon className="h-4 w-4 text-muted-foreground" />
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Your Items section */}
                      {inventoryItems && inventoryItems.length > 0 && (
                        <div
                          className={cn('py-2', groupResults?.length && 'border-t border-border')}
                        >
                          <p className="px-4 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                            <Trans>Your Items</Trans>
                          </p>
                          <ul>
                            {inventoryItems.map((item) => (
                              <li key={item.id}>
                                <button
                                  type="button"
                                  onClick={() => handleItemSelect(item)}
                                  className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm hover:bg-accent transition-colors"
                                >
                                  {item.product?.imageUrl ? (
                                    <img
                                      src={item.product.imageUrl}
                                      alt=""
                                      className="h-10 w-10 rounded-lg object-cover"
                                    />
                                  ) : (
                                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        className="h-5 w-5 text-muted-foreground"
                                      >
                                        <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
                                        <path d="M3 6h18" />
                                        <path d="M16 10a4 4 0 0 1-8 0" />
                                      </svg>
                                    </div>
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-foreground truncate">
                                      {item.customName ||
                                        (item.product
                                          ? resolveProductName(item.product)
                                          : t`Unknown`)}
                                    </p>
                                    <p className="text-xs text-muted-foreground truncate">
                                      {item.activeQuantity} {item.unit}
                                      {item.storageLocation && ` · ${item.storageLocation.name}`}
                                    </p>
                                  </div>
                                  <span className="text-xs text-fresh font-medium">
                                    <Trans>In Stock</Trans>
                                  </span>
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Add to Inventory section */}
                      {productsToAdd && productsToAdd.length > 0 && (
                        <div
                          className={cn('py-2', inventoryItems?.length && 'border-t border-border')}
                        >
                          <p className="px-4 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                            <Trans>Add to Inventory</Trans>
                          </p>
                          <ul>
                            {productsToAdd.map((product) => (
                              <li key={product.id}>
                                <button
                                  type="button"
                                  onClick={() => handleAddProduct(product)}
                                  className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm hover:bg-accent transition-colors"
                                >
                                  {product.imageUrl ? (
                                    <img
                                      src={product.imageUrl}
                                      alt=""
                                      className="h-10 w-10 rounded-lg object-cover"
                                    />
                                  ) : (
                                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        className="h-5 w-5 text-muted-foreground"
                                      >
                                        <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
                                      </svg>
                                    </div>
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium text-foreground truncate">
                                      {resolveProductName(product)}
                                    </p>
                                    {product.brand && (
                                      <p className="text-xs text-muted-foreground truncate">
                                        {product.brand}
                                      </p>
                                    )}
                                  </div>
                                  <span className="flex items-center gap-1 text-xs text-primary font-medium">
                                    <PlusIcon className="h-3.5 w-3.5" />
                                    <Trans>Add</Trans>
                                  </span>
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* No results */}
                      {(!groupResults || groupResults.length === 0) &&
                        (!inventoryItems || inventoryItems.length === 0) &&
                        (!productsToAdd || productsToAdd.length === 0) && (
                          <div className="px-4 py-3 text-sm text-muted-foreground">
                            <Trans>No items or products found</Trans>
                          </div>
                        )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right section */}
          <div className="flex items-center gap-2">
            {/* Household selector */}
            <Menu as="div" className="relative">
              <MenuButton className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-foreground hover:bg-accent transition-colors">
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10">
                  <HomeIcon aria-hidden="true" className="h-4 w-4 text-primary" />
                </div>
                <span className="hidden sm:block max-w-28 truncate">
                  {householdsLoading ? t`Loading...` : selectedHousehold?.name || t`Select`}
                </span>
                <ChevronDownIcon
                  aria-hidden="true"
                  className="hidden sm:block h-4 w-4 text-muted-foreground"
                />
              </MenuButton>

              <MenuItems
                transition
                className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-lg bg-popover border border-border shadow-lg transition data-closed:scale-95 data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-75 data-leave:ease-in"
              >
                <div className="p-2">
                  <p className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                    <Trans>Switch household</Trans>
                  </p>
                  {households.length === 0 ? (
                    <div className="px-2 py-3 text-sm text-muted-foreground">
                      <Trans>No households yet</Trans>
                    </div>
                  ) : (
                    households.map((household) => (
                      <MenuItem key={household.id}>
                        <button
                          onClick={() => selectHousehold(household.id)}
                          className={cn(
                            'flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors data-focus:bg-accent',
                            selectedHousehold?.id === household.id
                              ? 'text-primary font-medium'
                              : 'text-foreground'
                          )}
                        >
                          <div
                            className={cn(
                              'flex h-8 w-8 items-center justify-center rounded-lg',
                              selectedHousehold?.id === household.id ? 'bg-primary/10' : 'bg-muted'
                            )}
                          >
                            <HomeIcon
                              aria-hidden="true"
                              className={cn(
                                'h-4 w-4',
                                selectedHousehold?.id === household.id
                                  ? 'text-primary'
                                  : 'text-muted-foreground'
                              )}
                            />
                          </div>
                          <span className="flex-1 truncate text-left">{household.name}</span>
                          {selectedHousehold?.id === household.id && (
                            <CheckIcon className="h-4 w-4 text-primary" />
                          )}
                        </button>
                      </MenuItem>
                    ))
                  )}
                </div>
                <div className="border-t border-border p-2">
                  <MenuItem>
                    <Link
                      to="/households"
                      className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm text-muted-foreground data-focus:bg-accent data-focus:text-foreground transition-colors"
                    >
                      <Cog6ToothIcon className="h-4 w-4" />
                      <Trans>Manage households</Trans>
                    </Link>
                  </MenuItem>
                </div>
              </MenuItems>
            </Menu>

            {/* Notifications */}
            <NotificationDropdown />

            {/* Profile dropdown */}
            <Menu as="div" className="relative">
              <MenuButton className="flex items-center rounded-lg p-1 hover:bg-accent transition-colors">
                <span className="sr-only">
                  <Trans>Open user menu</Trans>
                </span>
                <Avatar size="sm" className="ring-2 ring-border">
                  <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
                    {getInitials()}
                  </AvatarFallback>
                </Avatar>
              </MenuButton>

              <MenuItems
                transition
                className="absolute right-0 z-10 mt-2 w-64 origin-top-right rounded-lg bg-popover border border-border shadow-lg transition data-closed:scale-95 data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-75 data-leave:ease-in"
              >
                {/* User info */}
                <div className="p-4 border-b border-border">
                  <div className="flex items-center gap-3">
                    <Avatar size="default">
                      <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                        {getInitials()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground truncate">{userName}</p>
                      <p className="text-sm text-muted-foreground truncate">{userEmail}</p>
                    </div>
                  </div>
                </div>

                {/* Theme selector */}
                <div className="p-3 border-b border-border">
                  <p className="mb-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    <Trans>Theme</Trans>
                  </p>
                  <div className="flex gap-1 p-1 rounded-lg bg-muted">
                    {themeOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setTheme(option.value)}
                        className={cn(
                          'flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-all',
                          theme === option.value
                            ? 'bg-background text-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground'
                        )}
                      >
                        <option.icon aria-hidden="true" className="h-4 w-4" />
                        <span className="hidden sm:inline">{option.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Menu items */}
                <div className="p-2">
                  {isAdmin && (
                    <MenuItem>
                      <Link
                        to="/admin/dashboard"
                        className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm text-foreground data-focus:bg-accent transition-colors"
                      >
                        <WrenchScrewdriverIcon
                          aria-hidden="true"
                          className="h-5 w-5 text-orange-500"
                        />
                        <Trans>Admin Console</Trans>
                      </Link>
                    </MenuItem>
                  )}
                  <MenuItem>
                    <Link
                      to="/settings"
                      className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm text-foreground data-focus:bg-accent transition-colors"
                    >
                      <Cog6ToothIcon aria-hidden="true" className="h-5 w-5 text-muted-foreground" />
                      <Trans>Settings</Trans>
                    </Link>
                  </MenuItem>
                  <MenuItem>
                    <button
                      onClick={handleSignOut}
                      className="flex w-full items-center gap-2 rounded-md px-2 py-2 text-sm text-foreground data-focus:bg-accent transition-colors"
                    >
                      <ArrowRightStartOnRectangleIcon
                        aria-hidden="true"
                        className="h-5 w-5 text-muted-foreground"
                      />
                      <Trans>Sign out</Trans>
                    </button>
                  </MenuItem>
                </div>
              </MenuItems>
            </Menu>
          </div>
        </div>
      </div>

      {/* Mobile menu panel */}
      <PopoverPanel as="nav" aria-label={t`Global`} className="lg:hidden border-t border-border">
        {/* Mobile search */}
        <div className="p-4 border-b border-border">
          <div className="relative">
            <MagnifyingGlassIcon
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground"
            />
            <input
              name="mobile-search"
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setShowSearchResults(true);
              }}
              placeholder={t`Search inventory or add items...`}
              className="block w-full rounded-lg border border-border bg-muted/50 py-2.5 pl-10 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:bg-background focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          {/* Mobile search results dropdown */}
          {showSearchResults && searchQuery.length >= 2 && (
            <div className="mt-2 rounded-lg bg-popover border border-border shadow-lg animate-in max-h-96 overflow-y-auto">
              {isSearching ? (
                <div className="flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  <Trans>Searching...</Trans>
                </div>
              ) : (
                <>
                  {groupResults && groupResults.length > 0 && (
                    <div className="py-2">
                      <p className="px-4 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        <Trans>Groups</Trans>
                      </p>
                      <ul>
                        {groupResults.slice(0, 5).map((group) => (
                          <li key={group.id}>
                            <button
                              type="button"
                              onClick={() => handleGroupSelect(group)}
                              className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm hover:bg-accent transition-colors"
                            >
                              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted text-lg">
                                {group.icon || '📦'}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-foreground truncate">{group.name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {group.memberCount}{' '}
                                  <Plural
                                    value={group.memberCount}
                                    one="product"
                                    other="products"
                                  />
                                </p>
                              </div>
                              <ChevronRightIcon className="h-4 w-4 text-muted-foreground" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {inventoryItems && inventoryItems.length > 0 && (
                    <div className={cn('py-2', groupResults?.length && 'border-t border-border')}>
                      <p className="px-4 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        <Trans>Your Items</Trans>
                      </p>
                      <ul>
                        {inventoryItems.map((item) => (
                          <li key={item.id}>
                            <button
                              type="button"
                              onClick={() => handleItemSelect(item)}
                              className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm hover:bg-accent transition-colors"
                            >
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-foreground truncate">
                                  {item.customName ||
                                    (item.product ? resolveProductName(item.product) : t`Unknown`)}
                                </p>
                                <p className="text-xs text-muted-foreground truncate">
                                  {item.activeQuantity} {item.unit}
                                  {item.storageLocation && ` · ${item.storageLocation.name}`}
                                </p>
                              </div>
                              <span className="text-xs text-fresh font-medium">
                                <Trans>In Stock</Trans>
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {productsToAdd && productsToAdd.length > 0 && (
                    <div className={cn('py-2', inventoryItems?.length && 'border-t border-border')}>
                      <p className="px-4 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        <Trans>Add to Inventory</Trans>
                      </p>
                      <ul>
                        {productsToAdd.map((product) => (
                          <li key={product.id}>
                            <button
                              type="button"
                              onClick={() => handleAddProduct(product)}
                              className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm hover:bg-accent transition-colors"
                            >
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-foreground truncate">
                                  {resolveProductName(product)}
                                </p>
                              </div>
                              <PlusIcon className="h-4 w-4 text-muted-foreground" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="p-2">
          {navigation.map((item) => {
            const isActive = currentPath === item.href || currentPath.startsWith(item.href + '/');
            return (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2.5 text-base font-medium transition-colors',
                  isActive
                    ? 'bg-primary text-primary-foreground'
                    : 'text-foreground hover:bg-accent'
                )}
              >
                {item.name}
              </Link>
            );
          })}
        </div>

        {/* Mobile user section */}
        <div className="border-t border-border p-4">
          <div className="flex items-center gap-3 mb-4">
            <Avatar size="default">
              <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                {getInitials()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-foreground truncate">{userName}</div>
              <div className="text-sm text-muted-foreground truncate">{userEmail}</div>
            </div>
          </div>

          {/* Mobile household selector */}
          <div className="mb-4">
            <p className="mb-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
              <Trans>Household</Trans>
            </p>
            <div className="space-y-1">
              {households.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">
                  <Trans>No households yet</Trans>
                </p>
              ) : (
                households.map((household) => (
                  <button
                    key={household.id}
                    onClick={() => selectHousehold(household.id)}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors',
                      selectedHousehold?.id === household.id
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-accent text-foreground'
                    )}
                  >
                    <HomeIcon aria-hidden="true" className="h-4 w-4" />
                    <span className="truncate">{household.name}</span>
                    {selectedHousehold?.id === household.id && (
                      <CheckIcon className="ml-auto h-4 w-4" />
                    )}
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Mobile theme selector */}
          <div className="mb-4">
            <p className="mb-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
              <Trans>Theme</Trans>
            </p>
            <div className="flex gap-1 p-1 rounded-lg bg-muted">
              {themeOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setTheme(option.value)}
                  className={cn(
                    'flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-all',
                    theme === option.value
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground'
                  )}
                >
                  <option.icon aria-hidden="true" className="h-4 w-4" />
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Mobile menu actions */}
          <div className="space-y-1 pt-4 border-t border-border">
            {isAdmin && (
              <Link
                to="/admin/dashboard"
                className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-base font-medium text-foreground hover:bg-accent transition-colors"
              >
                <WrenchScrewdriverIcon aria-hidden="true" className="h-5 w-5 text-orange-500" />
                <Trans>Admin Console</Trans>
              </Link>
            )}
            <Link
              to="/settings"
              className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-base font-medium text-foreground hover:bg-accent transition-colors"
            >
              <Cog6ToothIcon aria-hidden="true" className="h-5 w-5 text-muted-foreground" />
              <Trans>Settings</Trans>
            </Link>
            <button
              onClick={handleSignOut}
              className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-base font-medium text-foreground hover:bg-accent transition-colors"
            >
              <ArrowRightStartOnRectangleIcon
                aria-hidden="true"
                className="h-5 w-5 text-muted-foreground"
              />
              <Trans>Sign out</Trans>
            </button>
          </div>
        </div>
      </PopoverPanel>
    </Popover>
  );
}
